import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { DragSourceMonitor, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';

import { Cheque as ChequeType } from '@baemingo/table-events/dist/types';

import ReceiptIcon from '~/Assets/icons/receipt.svg';
import { useBridgeApi, useSelector, useStaffCan } from '~Hooks';
import { isSplitChequeCrashFixCompany, runDndDrop } from '~splitChequeCrashFix';
import { COLORS } from '~Scenes/Handy/styles';
import { useTableApi } from '~Components/TableUniverse';
import { DragTypes } from '~ComponentsImproved/TSR/Types';

import { createPortal } from 'react-dom';
import DragPreview from '~ComponentsImproved/TSR/DragPreview';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import FitStaffName from './FitStaffName';
import { Box } from '~Styles';
import Member from './Member';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';

const ChequeItem = styled.div<{
  selected: boolean;
  isDragging: boolean;
  isOver: boolean;
  canDrop: boolean;
  isPreview: boolean;
  isOnTablesView: boolean;
  canMutate: boolean;
}>`
  position: relative;
  display: ${({ isOnTablesView, isPreview }) => (isOnTablesView && !isPreview ? 'none' : 'flex')};
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  padding: 6px 4px;
  max-height: 88px;
  height: 100%;
  box-sizing: border-box;
  border-radius: 4px;
  border: 2px solid;
  border-color: ${(props) => (props.selected ? 'var(--colorUIYellow)' : 'transparent')};

  font-size: 14px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;

  background: ${({ isOver, canDrop }) =>
    isOver && canDrop ? 'var(--colorUIYellow)' : 'var(--colorBlueGray200)'};
  box-shadow: ${(props) => (props.selected ? '0px 0px 16px 0px rgba(0, 0, 0, 0.32)' : 'none')};
  color: ${(props) => (props.selected ? COLORS.blueGray1300 : COLORS.blueGray800)};

  transition: opacity 0.2s ease-in-out;

  animation: new-check ${400}ms cubic-bezier(0.28, 1.81, 0.53, 1.02);

  opacity: ${({ isDragging, isPreview, canMutate }) =>
    isDragging && !isPreview ? 0 : canMutate ? 1 : 0.6};

  z-index: ${({ canDrop }) => (canDrop ? 3 : 'auto')};
`;

const ChequeIcon = styled.div<{ selected?: boolean }>`
  height: 16px;
  width: 16px;
  mask-image: url(${ReceiptIcon});
  mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  margin-bottom: 4px;
  background-color: ${(props) => (props.selected ? COLORS.blueGray1100 : COLORS.blueGray600)};
`;

type DraggableCheque = {
  type: 'cheque';
  id: string;
};

interface Props {
  cheque: ChequeType;
  selected: boolean;
  onSelect: (cheque: ChequeType) => void;
  onMerge: (chequeId: string, targetId: string) => void;
  isOnTablesView: boolean;
  setIsDragging: (isDragging: boolean) => void;
}

const Cheque: React.FC<Props> = ({
  cheque,
  selected,
  onSelect,
  onMerge,
  isOnTablesView,
  setIsDragging,
}: Props) => {
  const bridgeApi = useBridgeApi();
  const staffCan = useStaffCan();
  const api = useTableApi();
  // only defer the drop mutation for the affected company
  const useFix = isSplitChequeCrashFixCompany(useSelector((state) => state.company));
  const staffCanMutate = staffCan.mutateCheque(cheque.id);
  const table = useTableContext();
  const { selectedCheque, setSelectedCheque } = useChequeContext();

  // This is called when another cheque gets dropped on THIS cheque
  const handleDrop = (droppedCheque: DraggableCheque) => {
    onMerge(droppedCheque.id, cheque.id);
  };

  function handleClick(e: any) {
    e.stopPropagation();
    onSelect(cheque);
  }

  const [{ isDragging }, drag] = useDrag({
    canDrag: !!staffCanMutate,
    item: {
      type: DragTypes.CHEQUE,
      id: cheque.id,
      table: {
        id: table.selectedTable?.id,
        name: table.selectedTable?.code,
      },
    },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: !!monitor.isDragging(),
      initialOffset: monitor.getInitialSourceClientOffset(),
      currentOffset: monitor.getSourceClientOffset(),
      clientOffset: monitor.getClientOffset(),
    }),
    end: (item, monitor) => {
      const didDrop = monitor.didDrop();
      if (!didDrop && !selectedCheque) {
        // If the cheque is dropped outside of a table in the tables view, we select the cheque again to go back to where the user was
        setTimeout(() => {
          setSelectedCheque(cheque);
          // The delay is to make it easier for the person to see what happened
        }, 300);
      }
    },
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [DragTypes.CHEQUE, DragTypes.ITEM],
    canDrop: (item: any) => {
      if (item.type === DragTypes.ITEM) {
        return staffCanMutate && !selected;
      } else if (item.type === DragTypes.CHEQUE) {
        return staffCanMutate && item.id !== cheque.id;
      }
      return false;
    },
    drop: (data) => {
      // Defer the mutation until the dnd drop transaction has completed.
      // Mutating synchronously re-renders and unregisters drop targets
      // mid-transaction, crashing react-dnd with "Expected to find a valid target."
      runDndDrop(useFix, () => {
        switch (data.type) {
          case DragTypes.ITEM:
            api.moveManyItemsToCheque(data.selectedItems, cheque.id);
            bridgeApi.vibrate('impactHeavy');

            break;
          case DragTypes.CHEQUE:
            handleDrop(data);
            bridgeApi.vibrate('impactHeavy');

            break;
          default:
            break;
        }
      });
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  useEffect(() => {
    if (isOver) {
      bridgeApi.vibrate('textHandleMove');
    }
  }, [isOver]);

  useEffect(() => {
    setIsDragging(isDragging);
  }, [isDragging]);

  const renderItem = (preview?: boolean) => {
    return (
      <ChequeItem
        ref={(node) => drag(drop(node))}
        isPreview={preview ?? false}
        isOnTablesView={isOnTablesView}
        selected={selected}
        onClick={handleClick}
        isDragging={isDragging}
        isOver={isOver}
        canDrop={canDrop}
        canMutate={staffCanMutate}>
        {cheque?.userId && <Member />}
        <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
          <ChequeIcon selected={selected} />
          {cheque.name ?? cheque.id.substring(0, 4)}
        </Box>
        {cheque?.owner && <FitStaffName staffId={cheque.owner} />}
      </ChequeItem>
    );
  };

  return (
    <>
      {/* Portal is needed because of the page css translate and to move cheque to a table */}
      {isDragging &&
        createPortal(
          <DragPreview
            type={DragTypes.CHEQUE}
            renderItem={renderItem(true)}
            styles={{
              width: '64px',
              height: '76px',
            }}
          />,
          document.body,
        )}
      {renderItem()}
    </>
  );
};

export default Cheque;
